import en from "../../../i18n/languages/en.json";
import tr from "../../../i18n/languages/tr.json";
import pl from "../../../i18n/languages/pl.json";
import { useEffect, useState } from "react";
import { LANGUAGE_CODES } from "../../../routes";

type TranslationsType = typeof en;
export type PageKey = keyof TranslationsType;

export const useSeo = (pageKey: PageKey) => {
  const [language, setLanguage] = useState(
    localStorage.getItem("language") ?? LANGUAGE_CODES.TR
  );

  useEffect(() => {
    const handleLangChange = () => {
      setLanguage(localStorage.getItem("language") ?? LANGUAGE_CODES.TR);
    };

    window.addEventListener("storage", handleLangChange);
    window.addEventListener("local-storage-language-change", handleLangChange);

    return () => {
      window.removeEventListener("storage", handleLangChange);
      window.removeEventListener(
        "local-storage-language-change",
        handleLangChange
      );
    };
  }, []);

  useEffect(() => {
    let translations;
    switch (language) {
      case LANGUAGE_CODES.EN:
        translations = en;
        break;
      case LANGUAGE_CODES.PL:
        translations = pl;
        break;
      default:
        translations = tr;
        break;
    }

    const title = translations[pageKey]?.seo?.title ?? "";
    const description = translations[pageKey]?.seo?.description ?? "";
    const keywords = translations[pageKey]?.seo?.keywords ?? "";

    // Set document title
    document.title = title;

    // Update or create meta description
    let metaDesc = document.querySelector("meta[name='description']");
    if (metaDesc) {
      metaDesc.setAttribute("content", description);
    } else {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      metaDesc.setAttribute("content", description);
      document.head.appendChild(metaDesc);
    }

    // Update or create meta keywords
    let metaKeywords = document.querySelector("meta[name='keywords']");
    if (metaKeywords) {
      metaKeywords.setAttribute("content", keywords);
    } else {
      metaKeywords = document.createElement("meta");
      metaKeywords.setAttribute("name", "keywords");
      metaKeywords.setAttribute("content", keywords);
      document.head.appendChild(metaKeywords);
    }

    // Update or create Open Graph meta tags
    const ogTags = [
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: language },
      { property: "og:site_name", content: "Zuzanna Bilici - Translator" },
    ];

    ogTags.forEach(({ property, content }) => {
      let ogTag = document.querySelector(`meta[property='${property}']`);
      if (ogTag) {
        ogTag.setAttribute("content", content);
      } else {
        ogTag = document.createElement("meta");
        ogTag.setAttribute("property", property);
        ogTag.setAttribute("content", content);
        document.head.appendChild(ogTag);
      }
    });

    // Update or create Twitter Card meta tags
    const twitterTags = [
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ];

    twitterTags.forEach(({ name, content }) => {
      let twitterTag = document.querySelector(`meta[name='${name}']`);
      if (twitterTag) {
        twitterTag.setAttribute("content", content);
      } else {
        twitterTag = document.createElement("meta");
        twitterTag.setAttribute("name", name);
        twitterTag.setAttribute("content", content);
        document.head.appendChild(twitterTag);
      }
    });

    // Update language attribute
    const htmlElement = document.documentElement;
    htmlElement.setAttribute("lang", language);
  }, [pageKey, language]);
};
